import React, { useState } from "react";
import * as Lucide from "lucide-react";
import { Play, X, ExternalLink, Image as ImageIcon, Video as VideoIcon } from "lucide-react";

export function VideoModal({ media, onClose }) {
  if (!media) return null;
  const isEmbedIframe = media.source === "embed" && ["youtube", "vimeo"].includes(media.provider);
  const isExternalLink = media.source === "embed" && !isEmbedIframe;
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" data-testid="video-modal" onClick={onClose}>
      <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="video-modal-close" className="absolute -top-10 right-0 text-white/70 hover:text-white"><X className="w-7 h-7" /></button>
        <div className="bg-black rounded-sm overflow-hidden aspect-video">
          {media.media_type === "photo" ? (
            <img src={media.url} alt={media.title} className="w-full h-full object-contain" />
          ) : isEmbedIframe ? (
            <iframe src={media.embed_url} title={media.title} className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          ) : isExternalLink ? (
            <div className="w-full h-full grid place-items-center text-white">
              <a href={media.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#E63946] px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm">Open Video <ExternalLink className="w-4 h-4" /></a>
            </div>
          ) : (
            <video src={media.url} controls autoPlay className="w-full h-full" />
          )}
        </div>
        {media.title && <div className="mt-3 text-white"><div className="font-display uppercase tracking-tight text-lg">{media.title}</div><div className="text-xs text-white/60">{[media.video_type, media.event_name].filter(Boolean).join(" · ")}</div></div>}
      </div>
    </div>
  );
}

export function MediaThumb({ media, onClick, testid }) {
  const thumb = media.thumbnail_url || (media.media_type === "photo" ? media.url : "");
  return (
    <button data-testid={testid} onClick={onClick} className="group text-left bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-[#E63946] transition-colors">
      <div className="aspect-video bg-[#0A192F] relative grid place-items-center overflow-hidden">
        {thumb ? <img src={thumb} alt={media.title} className="w-full h-full object-cover" loading="lazy" /> : (media.media_type === "photo" ? <ImageIcon className="w-8 h-8 text-white/30" /> : <VideoIcon className="w-8 h-8 text-white/30" />)}
        {media.media_type !== "photo" && <span className="absolute inset-0 grid place-items-center"><span className="w-11 h-11 rounded-full bg-[#E63946]/90 grid place-items-center group-hover:scale-110 transition-transform"><Play className="w-5 h-5 text-white ml-0.5" /></span></span>}
        {media.video_type && <span className="absolute top-1.5 left-1.5 text-[9px] font-semibold uppercase tracking-wider bg-black/60 text-white px-1.5 py-0.5 rounded-sm">{media.video_type}</span>}
      </div>
      <div className="p-2.5">
        <div className="text-xs font-semibold truncate group-hover:text-[#E63946]">{media.title || media.video_type || "Video"}</div>
        <div className="text-[10px] text-slate-400 truncate">{[media.event_name, media.event_date ? new Date(media.event_date).toLocaleDateString() : ""].filter(Boolean).join(" · ") || "—"}</div>
      </div>
    </button>
  );
}

export function FeaturedVideo({ media, onOpen }) {
  if (!media) return null;
  const thumb = media.thumbnail_url || (media.media_type === "photo" ? media.url : "");
  return (
    <div className="mb-8" data-testid="featured-video">
      <div className="label-overline text-[#E63946] mb-2 flex items-center gap-1"><Lucide.Star className="w-3.5 h-3.5" /> Featured</div>
      <button onClick={() => onOpen(media)} className="group w-full text-left rounded-sm overflow-hidden border border-slate-200 hover:border-[#E63946] transition-colors">
        <div className="aspect-video bg-[#0A192F] relative grid place-items-center overflow-hidden">
          {thumb ? <img src={thumb} alt={media.title} className="w-full h-full object-cover" /> : <VideoIcon className="w-12 h-12 text-white/30" />}
          {media.media_type !== "photo" && <span className="absolute inset-0 grid place-items-center"><span className="w-16 h-16 rounded-full bg-[#E63946]/90 grid place-items-center group-hover:scale-110 transition-transform"><Play className="w-7 h-7 text-white ml-1" /></span></span>}
        </div>
        <div className="p-3 bg-white">
          <div className="font-display uppercase tracking-tight">{media.title || media.video_type || "Featured Video"}</div>
          <div className="text-xs text-slate-400">{[media.video_type, media.event_name].filter(Boolean).join(" · ")}</div>
        </div>
      </button>
    </div>
  );
}

export function CareerTimeline({ items }) {
  const [asc, setAsc] = useState(false);
  if (!items || items.length === 0) return null;
  const ordered = asc ? [...items].reverse() : items;
  return (
    <div data-testid="career-timeline">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2"><Lucide.Milestone className="w-5 h-5 text-[#E63946]" /> Career Timeline</h3>
        <button data-testid="timeline-order-toggle" onClick={() => setAsc((s) => !s)} className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-[#0A192F]">{asc ? "Oldest first" : "Newest first"}</button>
      </div>
      <div className="mt-5 relative pl-6 border-l-2 border-slate-200 space-y-5">
        {ordered.map((e, i) => {
          const Icon = Lucide[e.icon] || Lucide.Star;
          const inner = (
            <>
              <span className="absolute -left-[31px] w-6 h-6 rounded-full bg-white border-2 border-[#E63946] grid place-items-center"><Icon className="w-3 h-3 text-[#E63946]" /></span>
              <div className="text-[11px] text-slate-400 font-data">{e.date ? new Date(e.date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : ""}</div>
              <div className="font-semibold text-sm text-[#0A192F]">{e.title}</div>
              {e.description && <div className="text-xs text-slate-500">{e.description}</div>}
            </>
          );
          return (
            <div key={i} data-testid={`timeline-event-${i}`} className="relative">
              {e.link ? <a href={e.link} className="block hover:opacity-80">{inner}</a> : inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}
